import * as THREE from "three";
import { InterpolationSink } from "../physics/physics_interpolation.js";
import RAPIER from "@dimforge/rapier3d-compat";
import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { PhysicsMass } from "../physics/physics_masses.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

interface DetachedSlice
{
  mesh: THREE.Mesh;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  outside_timer: number;
  fading: boolean;
  fade_timer: number;
}

export class WatermelonBlock extends BreakablePhysicsEntity implements LevelEntity
{
  public readonly kind = EntityKind.Watermelon;

  private static material: THREE.MeshLambertMaterial | null = null;

  // Block Lifecycle Constants
  public static readonly MIN_IMPACT_SPEED = 1.5;
  private static readonly FADE_DURATION = 0.8;
  private static readonly ARM_DELAY = 0.2;
  private static readonly OUTSIDE_TIMER_DURATION = 3.0;
  private static readonly GROUND_DESTROY_THRESHOLD_Y = 0.2;
  private static readonly FALL_DESTROY_THRESHOLD_Y = -2.0;

  // Physical Property Constants
  private static readonly LINEAR_DAMPING = 0.04;
  private static readonly ANGULAR_DAMPING = 0.35;
  private static readonly FRICTION = 0.45;
  private static readonly RESTITUTION = 0.08;
  private static readonly REST_LINVEL_SQ_THRESHOLD = 0.02;
  private static readonly REST_ANGVEL_SQ_THRESHOLD = 0.02;

  // Slice Specific Constants
  public static readonly TOTAL_SLICES = 4;
  private static readonly SLICE_LINEAR_DAMPING = 0.5;
  private static readonly SLICE_ANGULAR_DAMPING = 0.8;
  private static readonly SLICE_FRICTION = 0.4;
  private static readonly SLICE_RESTITUTION = 0.2;
  private static readonly SLICE_PUSH_SPEED = 0.15;
  private static readonly SLICE_UPWARD_IMPULSE = 0.05;
  private static readonly SLICE_INHERITED_VELOCITY_SCALE = 0.8;
  public static readonly SLICE_MAX_INHERITED_SPEED = 10;
  private static readonly SLICE_TORQUE_IMPULSE = 0.02;
  private static readonly SLICE_MIN_HALF_SIZE = 0.05;
  // Loose debris only. The whole block used to shrink its own collider by this
  // much too, which left it hanging above whatever it stood on — see the
  // constructor.
  private static readonly SLICE_COLLIDER_PADDING = 0.005;

  // Detached Slice Fade Constants
  private static readonly DETACHED_SLICE_FADE_DURATION = 1.0;
  private static readonly DETACHED_SLICE_OUTSIDE_DELAY = 2.0;

  private body_removed = false;
  private is_partially_broken = false;
  private outside_pending = false;
  private outside_timer = 0;
  private fade_timer = 0;

  private slices: THREE.Mesh[];
  private detached_slices: DetachedSlice[] = [];

  private cached_pos = new THREE.Vector3();

  constructor( position: THREE.Vector3, rotation?: { x?: number; y?: number; z?: number }, surface_tilt_rad = 0 )
  {
    const slices: THREE.Mesh[] = [];
    const mesh = new THREE.Group();
    mesh.add( WatermelonBlock.createVisual( slices ) );

    // Body and collider come from BreakablePhysicsEntity now, like every other
    // block — so the body gets the stack's extra solver iterations, and the box
    // is the full cell with no padding. The 5 mm padding this used to subtract on
    // all six sides left every melon hanging 5 mm above its support (rows are
    // stacked exactly PhysicsCube.getSize().y apart), so at level start the whole
    // wall of them dropped at once and kicked the tower over. The collider is
    // still declared by hand rather than measured off the mesh: it has to carry
    // the collision groups that keep the block from colliding with its own slices.
    const target_size = PhysicsCube.getSize();
    super( mesh, position, {
      angular_damping: WatermelonBlock.ANGULAR_DAMPING,
      angular_rest_threshold_sq: WatermelonBlock.REST_ANGVEL_SQ_THRESHOLD,
      arm_delay: WatermelonBlock.ARM_DELAY,
      collider_desc: RAPIER.ColliderDesc.cuboid(
        target_size.x * 0.5,
        target_size.y * 0.5,
        target_size.z * 0.5
      )
        // Membership 1, Filter FFFD (exclude bit 1 - slices)
        .setCollisionGroups( 0x0001FFFD ),
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
      friction: WatermelonBlock.FRICTION,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      linear_damping: WatermelonBlock.LINEAR_DAMPING,
      linear_rest_threshold_sq: WatermelonBlock.REST_LINVEL_SQ_THRESHOLD,
      mass: PhysicsMass.watermelon_block,
      restitution: WatermelonBlock.RESTITUTION,
    } );

    this.slices = slices;
    this.mesh.position.copy( position );
  }

  /** Collects the model's slice meshes into `slices` — they are detached one by one on hit. */
  private static createVisual( slices: THREE.Mesh[] ): THREE.Object3D
  {
    const visual = ResourceManager.getModel( ResourceID.WatermelonModel );
    const material = WatermelonBlock.getMaterial();

    visual.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        // Filter out empty dummy meshes (e.g. parent groups mistakenly exported as meshes)
        if ( !child.geometry || !child.geometry.attributes.position || child.geometry.attributes.position.count < 10 ) return;

        child.castShadow = true;
        child.receiveShadow = false;
        child.material = material;
        slices.push( child );
      }
    } );

    visual.updateMatrixWorld( true );
    const base_size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    const target_size = PhysicsCube.getSize();
    visual.scale.x *= target_size.x / base_size.x;
    visual.scale.y *= target_size.y / base_size.y;
    visual.scale.z *= target_size.z / base_size.z;

    visual.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( visual ).getCenter( new THREE.Vector3() );
    visual.position.sub( center );

    return visual;
  }

  private static getMaterial(): THREE.MeshLambertMaterial
  {
    if ( !this.material )
    {
      this.material = new THREE.MeshLambertMaterial( {
        map: ResourceManager.getTexture( ResourceID.WatermelonTexture ),
        normalMap: ResourceManager.getNormalMap( ResourceID.WatermelonNormalMap ),
      } );
    }
    return this.material;
  }

  public isPartiallyBroken(): boolean
  {
    return this.is_partially_broken;
  }

  public getPosition(): THREE.Vector3
  {
    // Return cached position safely after the rigid body has been removed
    if ( this.body_removed ) return this.cached_pos.clone();
    const pos = this.body.translation();
    this.cached_pos.set( pos.x, pos.y, pos.z );
    return this.cached_pos.clone();
  }

  public canBeHit(): boolean
  {
    return !this.destroying && this.isArmed();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    if ( this.isPartiallyBroken() )
    {
      this.destroy();
    }
    else
    {
      Vfx3DManager.addCannonBallBlockImpact( point, BlockImpactMaterial.WATERMELON );
      const impact_dir = point.clone().sub( this.getPosition() ).normalize();
      this.detachFirstSlice( impact_dir );
    }
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    // Fireworks respect the watermelon's normal two-stage damage model:
    // first hit detaches one slice, the next one breaks the rest apart.
    if ( this.isPartiallyBroken() )
    {
      this.destroy();
    }
    else
    {
      const impact_dir = point.clone().sub( this.getPosition() ).normalize();
      this.detachFirstSlice( impact_dir );
    }
  }

  public checkSelfDestruct( dt: number, table: PhysicsTable ): void
  {
    if ( this.table_ref )
      this.checkDestroy( dt, this.table_ref );
    else
      this.checkDestroy( dt, table );
  }

  public playHitSound(): void
  {
    void audioController.playSound( AudioClips.PUMPKIN_HIT );
  }

  public playDropSound(): void
  {
    void audioController.playSound( AudioClips.PUMPKIN_HIT );
  }

  /** Safe wakeUp: no-op if the body was already removed from the physics world. */
  public wakeUp(): void
  {
    if ( !this.body_removed ) this.body.wakeUp();
  }

  /**
   * First hit: detaches 1 slice with Rapier3D physics to expose interior red pulp.
   */
  public detachFirstSlice( _impact_dir?: THREE.Vector3 ): void
  {
    if ( this.is_partially_broken || this.destroying ) return;
    this.is_partially_broken = true;
    this.resetArmDelay();

    // Force matrixWorld update so Box3 returns accurate 3D world positions
    this.mesh.updateMatrixWorld( true );

    if ( this.slices.length > 0 )
    {
      let best_index = 0;
      let max_z = -Infinity;

      // Always pick the slice closest to the player's camera (+Z in world space)
      this.slices.forEach( ( slice, index ) =>
      {
        const slice_box = new THREE.Box3().setFromObject( slice );
        const slice_center = slice_box.getCenter( new THREE.Vector3() );
        if ( slice_center.z > max_z )
        {
          max_z = slice_center.z;
          best_index = index;
        }
      } );

      const slice_mesh = this.slices.splice( best_index, 1 )[0];
      this.spawnDetachedSlicePhysics( slice_mesh );
    }

    this.playHitSound();
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  /**
   * Spawns an independent Rapier3D dynamic rigid body + collider for a detached watermelon slice.
   */
  private spawnDetachedSlicePhysics( slice_mesh: THREE.Mesh, start_fading = false ): void
  {
    const world_quat = new THREE.Quaternion();
    const world_scale = new THREE.Vector3();
    slice_mesh.getWorldQuaternion( world_quat );
    slice_mesh.getWorldScale( world_scale );

    // geometry.boundingBox is computed once and cached on the shared geometry object.
    // Transforming it via matrixWorld costs O(8 corner transforms) vs O(n vertices) for setFromObject.
    const geometry = slice_mesh.geometry as THREE.BufferGeometry;
    if ( !geometry.boundingBox ) geometry.computeBoundingBox();
    const local_box = geometry.boundingBox!;

    // world_origin: world-space position of the mesh's LOCAL pivot (origin of its coordinate system).
    // The mesh and body MUST share this same reference point so that the per-frame sync
    //   detached.mesh.position = body.translation
    // keeps the mesh correctly aligned with the collider.
    const world_origin = new THREE.Vector3();
    slice_mesh.getWorldPosition( world_origin );

    // world_pos (geometric center of the slice vertices) is used ONLY to compute
    // the outward push direction so each slice flies away from the watermelon center.
    const world_box = local_box.clone().applyMatrix4( slice_mesh.matrixWorld );
    const world_pos = world_box.getCenter( new THREE.Vector3() );

    // Collider half-extents in body-local space (= mesh local space, since body rotation = world_quat).
    // Use LOCAL bbox * world_scale — avoids AABB inflation from mesh rotation.
    const local_size = local_box.getSize( new THREE.Vector3() );
    const half_x = Math.max( WatermelonBlock.SLICE_MIN_HALF_SIZE, local_size.x * Math.abs( world_scale.x ) * 0.5 - WatermelonBlock.SLICE_COLLIDER_PADDING );
    const half_y = Math.max( WatermelonBlock.SLICE_MIN_HALF_SIZE, local_size.y * Math.abs( world_scale.y ) * 0.5 - WatermelonBlock.SLICE_COLLIDER_PADDING );
    const half_z = Math.max( WatermelonBlock.SLICE_MIN_HALF_SIZE, local_size.z * Math.abs( world_scale.z ) * 0.5 - WatermelonBlock.SLICE_COLLIDER_PADDING );

    // safe_y: body center must be high enough that the collider bottom stays above the floor.
    // Use the world AABB half-height as a conservative estimate of the collider's vertical extent.
    const world_half_h = ( world_box.max.y - world_box.min.y ) * 0.5;
    const safe_y = Math.max( world_origin.y, world_half_h + 0.01 );

    // Re-parent: move slice into world-space Group preserving its visual position exactly.
    slice_mesh.removeFromParent();
    slice_mesh.renderOrder = 10;
    slice_mesh.castShadow = true;
    slice_mesh.receiveShadow = false;
    SceneManager.world.add( slice_mesh );

    // Mesh positioned at its world pivot (origin) — matches body translation so sync works.
    slice_mesh.position.copy( world_origin );
    slice_mesh.quaternion.copy( world_quat );
    slice_mesh.scale.copy( world_scale );

    const slice_body = PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation( world_origin.x, safe_y, world_origin.z )
        // Rotation matches the mesh so body-local half-extents align with slice geometry
        .setRotation( { x: world_quat.x, y: world_quat.y, z: world_quat.z, w: world_quat.w } )
        .setLinearDamping( WatermelonBlock.SLICE_LINEAR_DAMPING )
        .setAngularDamping( WatermelonBlock.SLICE_ANGULAR_DAMPING )
    );
    slice_body.enableCcd( true );

    // Inherit existing velocity from parent watermelon body scaled by SLICE_INHERITED_VELOCITY_SCALE
    const parent_vel = this.body.linvel();
    const vel_vec = new THREE.Vector3( parent_vel.x, parent_vel.y, parent_vel.z ).multiplyScalar( WatermelonBlock.SLICE_INHERITED_VELOCITY_SCALE );
    if ( vel_vec.length() > WatermelonBlock.SLICE_MAX_INHERITED_SPEED )
    {
      vel_vec.setLength( WatermelonBlock.SLICE_MAX_INHERITED_SPEED );
    }
    slice_body.setLinvel( { x: vel_vec.x, y: vel_vec.y, z: vel_vec.z }, true );

    // The mesh geometry is offset from its pivot (world_origin). The rigid body is at the pivot.
    // We must offset the collider by the same amount in local space so it wraps the geometric center.
    const local_center = local_box.getCenter( new THREE.Vector3() );
    const collider_offset_x = local_center.x * Math.abs( world_scale.x );
    const collider_offset_y = local_center.y * Math.abs( world_scale.y );
    const collider_offset_z = local_center.z * Math.abs( world_scale.z );

    const slice_collider = PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( half_x, half_y, half_z )
        .setTranslation( collider_offset_x, collider_offset_y, collider_offset_z )
        .setFriction( WatermelonBlock.SLICE_FRICTION )
        .setRestitution( WatermelonBlock.SLICE_RESTITUTION )
        // Membership 2, Filter FFFC (exclude bit 0 - watermelon, and bit 1 - slices)
        .setCollisionGroups( 0x0002FFFC ),
      slice_body
    );
    // PhysicsMass is refreshed for each level before entities are built, so
    // derive this at split time instead of caching the module-load base value.
    slice_collider.setMass( PhysicsMass.watermelon_block / WatermelonBlock.TOTAL_SLICES );

    // Push the slice OUTWARDS horizontally and UPWARDS from the center of the watermelon
    const dir = world_pos.clone().sub( this.getPosition() ).normalize();

    const push_speed = WatermelonBlock.SLICE_PUSH_SPEED;
    const up_speed = WatermelonBlock.SLICE_UPWARD_IMPULSE;
    slice_body.applyImpulse(
      {
        x: dir.x * push_speed,
        y: up_speed + Math.max( 0, dir.y * push_speed ),
        z: dir.z * push_speed
      },
      true
    );
    slice_body.applyTorqueImpulse(
      {
        x: ( Math.random() - 0.5 ) * WatermelonBlock.SLICE_TORQUE_IMPULSE,
        y: ( Math.random() - 0.5 ) * WatermelonBlock.SLICE_TORQUE_IMPULSE,
        z: ( Math.random() - 0.5 ) * WatermelonBlock.SLICE_TORQUE_IMPULSE
      },
      true
    );

    this.detached_slices.push( {
      mesh: slice_mesh,
      body: slice_body,
      collider: slice_collider,
      outside_timer: WatermelonBlock.DETACHED_SLICE_OUTSIDE_DELAY,
      fading: start_fading,
      fade_timer: WatermelonBlock.DETACHED_SLICE_FADE_DURATION,
    } );
  }

  private table_ref: PhysicsTable | null = null;
  private previous_table_bottom_y: number | null = null;
  private drop_sound_played = false;

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    this.table_ref = table;
    if ( this.destroying ) return;
    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();
    const table_bottom_y = table.heightAboveSurface( pos ) - this.halfHeight();

    if ( !this.drop_sound_played && !is_outside && this.previous_table_bottom_y !== null
      && this.previous_table_bottom_y > 0.2 && table_bottom_y <= 0.2 )
    {
      this.playDropSound();
      this.drop_sound_played = true;
    }
    this.previous_table_bottom_y = table_bottom_y;

    if ( !is_outside )
    {
      this.outside_pending = false;
      return;
    }

    if ( bottom_y <= WatermelonBlock.GROUND_DESTROY_THRESHOLD_Y || pos.y < WatermelonBlock.FALL_DESTROY_THRESHOLD_Y )
    {
      this.destroy();
      return;
    }

    if ( !this.outside_pending && table.isBelowSurface( pos ) )
    {
      this.outside_pending = true;
      this.outside_timer = WatermelonBlock.OUTSIDE_TIMER_DURATION;
    }

    // Unconditional after the timer: a block parked above the ground (on the
    // table base or wedged on the leg) would otherwise stay outside_pending
    // forever, and isAtRest() === false would block the game-end check.
    if ( this.outside_pending && ( this.outside_timer -= dt ) <= 0 )
    {
      this.destroy();
    }
  }

  public isAlive(): boolean
  {
    return super.isAlive() || this.detached_slices.length > 0;
  }

  public hasPhysicsBody(): boolean
  {
    return !this.body_removed;
  }

  public isAtRest(): boolean
  {
    return !this.outside_pending && super.isAtRest();
  }

  private setSingleObjectOpacity( obj: THREE.Object3D, opacity: number ): void
  {
    obj.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      if ( !child.userData.materialCloned )
      {
        const cloneMat = ( m: THREE.Material ): THREE.Material =>
        {
          const cloned = m.clone();
          cloned.transparent = true;
          cloned.needsUpdate = true;
          return cloned;
        };

        child.material = Array.isArray( child.material )
          ? child.material.map( m => cloneMat( m ) )
          : cloneMat( child.material );
        child.userData.materialCloned = true;
      }
      const materials = Array.isArray( child.material ) ? child.material : [ child.material ];
      for ( const m of materials )
      {
        m.transparent = true;
        m.opacity = opacity;
        m.depthWrite = false;
      }
      child.castShadow = false;
    } );
  }

  private setOpacity( opacity: number ): void
  {
    this.setSingleObjectOpacity( this.mesh, opacity );
  }

  protected updateDestroying( dt: number ): void
  {
    this.fade_timer -= dt;
    this.setOpacity( Math.max( 0, this.fade_timer / WatermelonBlock.FADE_DURATION ) );
    if ( this.fade_timer <= 0 && this.slices.length === 0 )
    {
      this.markDead();
    }
  }

  public update( dt: number ): void
  {
    // destroy() takes the rigid body out of the world, so there is nothing left to
    // sync the mesh to — reading a removed body's transform is undefined. Run the
    // fade on its own instead of going through the base class's update.
    if ( this.body_removed )
      this.updateDestroying( dt );
    else
      super.update( dt );

    this.updateDetachedSlices( dt );
  }

  public collectInterpolated( add: InterpolationSink ): void
  {
    for ( const detached of this.detached_slices )
      add( detached.mesh, false );
  }

  /** Syncs detached slices and fades out the ones that hit the floor or timed out. */
  private updateDetachedSlices( dt: number ): void
  {
    for ( let i = this.detached_slices.length - 1; i >= 0; i-- )
    {
      const detached = this.detached_slices[i];
      const pos = detached.body.translation();
      const rot = detached.body.rotation();
      detached.mesh.position.set( pos.x, pos.y, pos.z );
      detached.mesh.quaternion.set( rot.x, rot.y, rot.z, rot.w );

      if ( detached.fading )
      {
        detached.fade_timer -= dt;
        const opacity = Math.max( 0, detached.fade_timer / WatermelonBlock.DETACHED_SLICE_FADE_DURATION );
        this.setSingleObjectOpacity( detached.mesh, opacity );
        if ( detached.fade_timer <= 0 )
        {
          SceneManager.world.remove( detached.mesh );
          try { PhysicsWorld.getWorld().removeRigidBody( detached.body ); } catch {}
          this.detached_slices.splice( i, 1 );
        }
        continue;
      }

      // Check if this detached slice fell off the table, hit the floor, or timer expired on table
      const bottom_y = pos.y - 0.1;
      if ( bottom_y <= WatermelonBlock.GROUND_DESTROY_THRESHOLD_Y || pos.y < WatermelonBlock.FALL_DESTROY_THRESHOLD_Y )
      {
        detached.fading = true;
      }
      else
      {
        // On table or in air: countdown timer to fade out cleanly
        detached.outside_timer -= dt;
        if ( detached.outside_timer <= 0 )
        {
          detached.fading = true;
        }
      }
    }
  }

  /**
   * Second hit or floor destruction: detaches all remaining slices with Rapier3D physics and starts fade-out.
   */
  public destroy(): void
  {
    if ( !this.beginDestroying() ) return;
    this.fade_timer = WatermelonBlock.FADE_DURATION;

    const break_pos = this.getPosition();

    // Detach all remaining slices into full Rapier3D physical objects with fading = true.
    // Must happen BEFORE the body goes away — each slice inherits its velocity.
    while ( this.slices.length > 0 )
    {
      const slice_mesh = this.slices.shift()!;
      this.spawnDetachedSlicePhysics( slice_mesh, true );
    }

    // Remove the main watermelon body from the physics world immediately so
    // its collider disappears as soon as the block breaks apart.
    if ( !this.body_removed )
    {
      this.cached_pos.copy( break_pos );
      this.body_removed = true;
      SceneManager.world.remove( this.mesh );
      try { PhysicsWorld.getWorld().removeRigidBody( this.body ); } catch {}
    }

    Vfx3DManager.addWatermelonBreak( break_pos );

    this.playHitSound();
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public dispose(): void
  {
    if ( this.body_removed )
    {
      // destroy() already removed the body — never hand a stale handle back to
      // Rapier, the slot may belong to somebody else by now.
      SceneManager.world.remove( this.mesh );
    }
    else
    {
      this.body_removed = true;
      super.dispose();
    }

    for ( const detached of this.detached_slices )
    {
      SceneManager.world.remove( detached.mesh );
      try { PhysicsWorld.getWorld().removeRigidBody( detached.body ); } catch {}
    }
    this.detached_slices = [];
  }
}
